<template>
  <div class="slide-controls">
    <!-- Navigation Arrows -->
    <div class="nav-arrows">
      <button
        class="nav-btn prev"
        :disabled="currentIndex === 0"
        @click="$emit('prev')"
        :aria-label="'Previous slide'"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <!-- Slide Counter -->
      <div class="slide-counter">
        <span class="current">{{ currentIndex + 1 }}</span>
        <span class="separator">/</span>
        <span class="total">{{ totalSlides }}</span>
      </div>

      <button
        class="nav-btn next"
        :disabled="currentIndex === totalSlides - 1"
        @click="$emit('next')"
        :aria-label="'Next slide'"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>

    <!-- Progress Dots -->
    <div class="progress-dots">
      <button
        v-for="(_, index) in totalSlides"
        :key="index"
        class="dot"
        :class="{ active: index === currentIndex, visited: index < currentIndex }"
        @click="$emit('goto', index)"
        :aria-label="`Go to slide ${index + 1}`"
      />
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons">
      <!-- Download PDF Button -->
      <button
        class="action-btn download-btn"
        :class="{ 'is-loading': isGeneratingPdf }"
        :disabled="isGeneratingPdf"
        @click="$emit('download-pdf')"
        :aria-label="isGeneratingPdf ? 'Generating PDF...' : 'Download as PDF'"
      >
        <svg v-if="!isGeneratingPdf" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M21 15V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V15M7 10L12 15M12 15L17 10M12 15V3"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else class="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" opacity="0.25"/>
          <path d="M12 2C6.47715 2 2 6.47715 2 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>

      <!-- Fullscreen Toggle -->
      <button
        class="action-btn fullscreen-btn"
        @click="$emit('fullscreen')"
        :aria-label="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
      >
        <svg v-if="!isFullscreen" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 3H5C3.89543 3 3 3.89543 3 5V8M21 8V5C21 3.89543 20.1046 3 19 3H16M16 21H19C20.1046 21 21 20.1046 21 19V16M3 16V19C3 20.1046 3.89543 21 5 21H8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 14H10V20M20 10H14V4M14 10L21 3M3 21L10 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuarterlySlideControls',
  props: {
    currentIndex: {
      type: Number,
      required: true
    },
    totalSlides: {
      type: Number,
      required: true
    },
    isFullscreen: {
      type: Boolean,
      default: false
    },
    isGeneratingPdf: {
      type: Boolean,
      default: false
    }
  }
};
</script>

<style lang="scss" scoped>
.slide-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  margin-top: 24px;
  padding: 16px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px -2px rgba(0, 0, 0, 0.08);
}

.nav-arrows {
  display: flex;
  align-items: center;
  gap: 16px;
}

.nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: #f5f5f5;
  color: #3d3d3d;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #7c3aed;
    color: #fff;
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
}

.slide-counter {
  font-family: 'JetBrains Mono', 'DM Sans', monospace;
  font-size: 14px;
  color: #6b6b6b;
  min-width: 60px;
  text-align: center;

  .current {
    font-weight: 700;
    color: #3d3d3d;
  }

  .separator {
    margin: 0 4px;
  }

  .total {
    color: #9e9e9e;
  }
}

.progress-dots {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dot {
  width: 8px;
  height: 8px;
  border: none;
  border-radius: 50%;
  background: #e0e0e0;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;

  &:hover {
    background: #a78bfa;
    transform: scale(1.2);
  }

  &.visited {
    background: #c4b5fd;
  }

  &.active {
    background: #7c3aed;
    width: 24px;
    border-radius: 4px;
  }
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #6b6b6b;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    color: #7c3aed;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.is-loading .spinner {
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

// Fullscreen mode adjustments - handled via prop in parent component

// Responsive adjustments
@media (max-width: 768px) {
  .slide-controls {
    gap: 16px;
    padding: 12px;
  }

  .progress-dots {
    display: none;
  }

  .nav-btn {
    width: 40px;
    height: 40px;
  }
}
</style>
